/**
 * Composition pour l'utilisateur que l'on gère (abonnement, connexion aux réseaux)
 */

import { ref, computed } from 'vue';
import axios from '@js/tools/axios';
/***/
import { useUserStore } from '@stores/user';
import { useAuthUserStore } from '@stores/auth';
import { useAuthAIThematicStore } from '@stores/auth/aiThematic';
import { useAuthAIAudienceStore } from '@stores/auth/aiAudience';
import { useDefaultGraphicalLineTemplateStore } from '@stores/graphicalLineTemplate/allGraphicalLineTemplate';
import { useSingleImageGraphicalLineTemplateStore } from '@stores/graphicalLineTemplate/singleImageGraphicalLineTemplate';
import { useCarouselGraphicalLineTemplateStore } from '@stores/graphicalLineTemplate/carouselGraphicalLineTemplate';
import { useAuthAIPostImageStore } from '@stores/auth/aiPostImage';
import { useThemeStore } from '@stores/theme';
import { useAuthPublicationStatisticsStore } from '@stores/auth/publicationStatistics';
import { useAuthSchedulePublicationStatisticsStore } from '@stores/auth/schedulePublicationStatistics';
import { useAuthTonePublicationStatisticsStore } from '@stores/auth/tonePublicationStatistics';
import { useSocialNetworkAccountStatisticStore } from '@stores/socialNetworkAccountsStatistic';
import { useSocialPublicationCommentStore } from '@stores/socialPublicationComment.js';

/**
 * Composition pour la gestion de l'utilisateur à gérer (peut être l'utilisateur connecté, ou un utilisateur client d'une agence)
 * @return object
 */
export function useManagingUser({}) {

    const userStore = useUserStore();
    const authUserStore = useAuthUserStore();
    /***/
    const allGraphicalLineTemplateStore = useDefaultGraphicalLineTemplateStore();
    const singleGraphicalLineTemplateStore = useSingleImageGraphicalLineTemplateStore();
    const carouselGraphicalLineTemplateStore = useCarouselGraphicalLineTemplateStore();
    /***/
    const aiPostImageStore = useAuthAIPostImageStore();
    const userThemeStore = useThemeStore();
    // Store pour les thématiques IA de l'utilisateur
    const authAIThematicStore = useAuthAIThematicStore();
    // Store pour les audiences IA de l'utilisateur
    const authAIAudienceStore = useAuthAIAudienceStore();
    // Store pour les statistiques d'un utilisateur
    const publicationStatisticsStore = useAuthPublicationStatisticsStore();
    const schedulePublicationStatisticsStore = useAuthSchedulePublicationStatisticsStore();
    const tonePublicationStatisticsStore = useAuthTonePublicationStatisticsStore();
    const socialNetworkAccountStatisticStore = useSocialNetworkAccountStatisticStore();
    // Store pour les commentaires des publications
    const socialPublicationCommentStore = useSocialPublicationCommentStore();

    const getManagingUser = computed(() => userStore.getCurrentManagingUser);
    const getAuthUser = computed(() => authUserStore.getUser);

    // Vrai si l'utilisateur qui est géré est un client
    const managingUserIsCustomer = computed(() => (getManagingUser.value?.type === 'CUSTOMER'));

    // Dernière analyse des statistiques de l'utilisateur que l'on gère
    const lastPublicationsStatisticsAnalysis = ref(false); // La valeur est à false tant qu'elle n'a pas été chargé

    /**
     * Retourne la dernière analyse des statistiques des publications de l'utilisateur connecté
     * @return Object|null
     */
    const getLastPublicationsStatisticsAnalysis = computed(() => (lastPublicationsStatisticsAnalysis.value || null));

    /**
     * Chargement de la dernière analyse des statistiques des publications de l'utilisateur connecté
     * @return Promise
     */
    const loadLastPublicationsStatisticsAnalysis = (() => {
        if(lastPublicationsStatisticsAnalysis.value !== false) {
            return Promise.resolve();
        }

        const userId = getManagingUser.value.id;

        return axios.get(`/api/users/${ userId }/tasks/publications/statistics/analysis`).then((response) => {
            if(response && response.data && response.data.success && response.data.analysis) {
                lastPublicationsStatisticsAnalysis.value = response.data.analysis;
                return Promise.resolve();
            }
            return Promise.reject();
        });
    });

    /**
     * URL de l'image de profil de l'utilisateur que l'on gère
     * @param string version small|medium
     * @return string|null
     */
    const getManagingUserProfilImageUrl = ((version) => {
        const profileImage = (getManagingUser.value?.profileImage ?? null);
        if (profileImage !== null) {
            return profileImage[version].url;
        }
        return '/images/empty/medium.webp';
    });

    /**
     * Nombre total de crédits de l'utilisateur que l'on gère
     * @return int|null
     */
    const managingUserTotalCredits = computed(() => {

        const creditsCounters = (getManagingUser.value?.credits ?? null);
        if(creditsCounters === null) {
            return null;
        }
        const values = Object.keys(creditsCounters).map((key) => creditsCounters[key]);
        return values.reduce(((value1, value2) => value1 + value2), 0);
    });

    /**
     * Réseaux de l'utilisateur que l'on gère
     * @return Array<string>
     */
    const managingUserNetworks = computed(() => {

        const socialIdentities = (getManagingUser.value?.socialNetworkIdentities ?? []).filter((identity) => identity.enabled);
        if(socialIdentities.length > 0) {
            return socialIdentities.map((identity) => identity.network);
        }

        const aiNetworks = ((getManagingUser.value?.settings?.aiNetworks) ?? {});
        return (Object.keys(aiNetworks).filter((networkName) => aiNetworks[networkName].enabled));
    });

    // Retourne si l'utilisateur que l'on gère à des réseaux
    const managingUserHasNetworks = computed(() => (managingUserNetworks.value.length > 0));

    // Maximum de publications qui peuvent être générées par semaine pour l'utilisateur que l'on gère
    const managingUserMaxPublicationsByWeek = computed(() => {
        // @todo @trial-period Adapté dans le cas ou le compte est free ou pendant une période d'essai
        const userSubscription = getUserSubscription();
        const subscriptionMaxPublication = (userSubscription !== null && userSubscription.status === 'ENABLED') ? userSubscription.maxPublicationsByWeek : 3;
        return subscriptionMaxPublication;
    });

    // Rafraichissement des données de l'utilisateur que l'on gère
    const refreshManagingUserData = (() => {
        const userId = getManagingUser.value.id;

        const requestPath = `/api/users/${ userId }/account`;
        return axios.get(requestPath).then((response) => {
            if(response?.data?.success && response?.data?.user) {
                updateManagingUser(response.data.user);
                return Promise.resolve();
            }
            return Promise.reject();
        });
    });

    // Méthode pour activer l'IA pour l'utilisateur à gérer
    const enableIAForManagingUser = (() => {
        const userId = getManagingUser.value.id;

        const requestPath = `/api/users/${ userId }/account/ai/enable`;
        return axios.post(requestPath, {
            enable: true
        }).then((response) => {
            if(response?.data?.success && response?.data?.user) {
                updateManagingUser(response.data.user);
                return Promise.resolve();
            }
            return Promise.reject();
        });

    });

    /**
     * Génération des audiences
     * @param Object society
     * @return Promise
     */
    const generateAudiencesForManagingUser = ((society) => {
        const userId = getManagingUser.value.id;

        const path = `/api/users/${ userId }/account/ai/generate-audiences`;

        return axios.post(path, {
            name: (society.name || society.commercialName),
            summary: society.summary
        }).then((response) => {
            if (response && response.data && response.data.success && response.data.user) {
                const newAudiences = (response.data.user.settings.aiAudiences ?? []);
                const currentManagingUser = Object.assign({}, JSON.parse(JSON.stringify(getManagingUser.value)));
                currentManagingUser.settings.aiAudiences = newAudiences;

                updateManagingUser(currentManagingUser);

                return Promise.resolve(newAudiences);
            }
            return Promise.reject();
        });

    });

    /**
     * Initialise le réseau pour le premier planning de l'utilisateur à gérer
     * @param object params (données de la société et réseau pour faire la requête)
     * @return Promise
     */
    const initFirstNetworkForManagingUser = ((params) => {
        const userId = getManagingUser.value.id;

        const path = `/api/users/${ userId }/account/ai/init-first-network`;

        return axios.post(path, params).then((response) => {
            if(response && response.data && response.data.success) {

                // Mise à jour des thématiques
                const newThematics = (response.data.aiThematics ?? []);
                if(newThematics) {
                    authAIThematicStore.setUserThematics(userId, newThematics);
                }

                // Mise à jour des audiences
                if(response.data.aiAudiences) {
                    authAIAudienceStore.setAudiences(response.data.aiAudiences);
                }

                // Mise à jour des suggestions d'URL d'actualités pour la société
                if(response.data.societyActualitySourcesSuggested) {
                    const currentUser = Object.assign({}, JSON.parse(JSON.stringify(getManagingUser.value)));
                    currentUser.society.actualitySourcesSuggested = response.data.societyActualitySourcesSuggested;
                    updateManagingUser(currentUser);
                }

                return Promise.resolve();
            }
            return Promise.reject({});
        }).catch((response) => {
            let errors = {};
            if(response && response.response && response.response.data && response.response.data.errors) {
                errors = response.response.data.errors;
            }
            return Promise.reject(errors);
        });
    });

    // Modifie l'utilisateur que l'on gère
    const setManagingUser = ((newUserData) => {
        userStore.setCurrentManagingUser(newUserData);
    });

    // Mise à jour des données de l'utilisateur que l'on gère
    const updateManagingUser = ((newUserData) => {
        const isAuthUser = (getAuthUser.value.ownerId === newUserData.ownerId);
        if(isAuthUser) {
            authUserStore.updateCurrentUserData(newUserData);
            setManagingUser(getAuthUser.value);
        } else {
            const isSameUser = (getManagingUser.value.ownerId === newUserData.ownerId);
            let newData = { ...newUserData };
            if(isSameUser) {
                newData = {
                    ...getManagingUser.value,
                    ...newUserData
                };
                setManagingUser(newData);
            }
        }
        const newTrialPeriodStatus = (newUserData.trialPeriod ?? null)
        if(newTrialPeriodStatus !== null) {
            userStore.setUserTrialPeriodStatus(newUserData.id, newTrialPeriodStatus);
        }
    });

    // Route de la gestion de l'abonnement de l'utilisateur que l'on gère
    const getUserSubscriptionRouteParams = computed(() => {

        const agencyCustomerId = (getManagingUser.value.agencyCustomerId ?? null);
        const authUser = getAuthUser.value;

        if(authUser.type === 'AGENCY_MANAGER' && agencyCustomerId !== null) {
            return {
                name: 'me.agency.customers.subscription',
                params: {
                    agencyCustomerId: agencyCustomerId
                }
            }
        }

        return {
            name: 'me.subscription.index'
        };
    });

    // Retourne l'abonnement de l'utilisateur que l'on gère
    const getUserSubscription = (() => {
        if(getManagingUser.value === null) {
            return null;
        }
        return userStore.getUserSubscriptionById(getManagingUser.value.id);
    });

    // Retourne si l'utilisateur a un compte actif
    const hasEnabledSubscription = (() => {
        const subscription = getUserSubscription();
        return (subscription !== null && subscription.status === 'ENABLED');
    });

    // Charge l'abonnement de l'utilisateur que l'on gère
    const loadUserSubscription = ((reload = false) => {
        if(getManagingUser.value === null) {
            return Promise.reject();
        }
        return userStore.loadCreditSubscriptionById(getManagingUser.value.id, reload).then((response) => {
            // On met à jour les compteurs des crédits s'ils sont renseignés
            const newCreditsCounters = (response?.data?.userCreditsCounters ?? null);
            if(newCreditsCounters !== null && getManagingUser.value !== null) {
                const newUserData = Object.assign({}, JSON.parse(JSON.stringify(getManagingUser.value)));
                newUserData.credits = newCreditsCounters;
                updateManagingUser(newUserData);
            }
            return Promise.resolve();
        });
    });

    // Modifie l'abonnement de l'utilisateur que l'on gère
    const setUserSubscription = ((newSubscriptionData) => {
        if(getManagingUser.value === null) {
            return;
        }
        userStore.updateUserSubscriptionById(getManagingUser.value.id, newSubscriptionData);
    });

    // Retourne si l'utilisateur qui l'on gère est autorisé à effectuer des actions sans abonnement
    const hasFreeSubscription = (() => {
        if(! getManagingUser.value || ! getManagingUser.value.freeSubscriptionUntil) {
            return false;
        }

        const currentDate = new Date();
        const freeUntil = new Date(getManagingUser.value.freeSubscriptionUntil);
        return (freeUntil >= currentDate);
    });

    /**
     * Vrai si le réseau en paramètre est connecté pour l'utilisateur authentifié
     * @param string networkName
     * @return bool
     */
    const managingUserHasThisNetwork = ((networkName) => {
        const identities = (getManagingUser.value?.socialNetworkIdentities || []);
        return (identities.findIndex((networkData) =>
            networkData.network.toLowerCase() === networkName.toLowerCase() &&
            networkData.enabled
        ) !== -1);
    });

    // Mise à jour de l'identité (enabled et network) dans les données de l'utilisateur que l'on gère
    const updateUserSocialNetworkIdentity = ((identity) => {
        const userData = Object.assign({}, JSON.parse(JSON.stringify(getManagingUser.value)));
        const networkIdentities = (userData.socialNetworkIdentities ?? []).filter((currentIdentity) => currentIdentity.network !== identity.network);
        networkIdentities.push(identity);
        userData.socialNetworkIdentities = networkIdentities;
        updateManagingUser(userData);
    });

    // Réinitialisation des resources chargées de l'utilisateur
    const resetManagingUserRessources = (() => {
        // Réinitialisation des listes de templates de charte graphique
        const graphicalLineTemplateStores = [
            allGraphicalLineTemplateStore,
            singleGraphicalLineTemplateStore,
            carouselGraphicalLineTemplateStore
        ];

        graphicalLineTemplateStores.forEach((store) => {
            store.resetTemplateList();
            store.resetRestorationState();
        });

        // Réinitialisation des images générées par l'IA
        aiPostImageStore.resetPostImageList();

        // Réinitialisation des thèmes
        userThemeStore.resetThemeList();

        // Réinitialisation des statistiques
        publicationStatisticsStore.resetPublicationsStatisticsList();
        schedulePublicationStatisticsStore.reset();
        tonePublicationStatisticsStore.resetTonesStatisticsList();
        socialNetworkAccountStatisticStore.reset();
        lastPublicationsStatisticsAnalysis.value = false;

        // Réinitialisation des commentaires des publications
        socialPublicationCommentStore.reset();

    });

    return {
        getManagingUser,
        managingUserIsCustomer,
        setManagingUser,
        updateManagingUser,
        getUserSubscription,
        loadUserSubscription,
        hasEnabledSubscription,
        setUserSubscription,
        hasFreeSubscription,
        resetManagingUserRessources,
        updateUserSocialNetworkIdentity,
        managingUserHasThisNetwork,
        refreshManagingUserData,
        enableIAForManagingUser,
        generateAudiencesForManagingUser,
        initFirstNetworkForManagingUser,
        managingUserNetworks,
        managingUserHasNetworks,
        getManagingUserProfilImageUrl,
        managingUserTotalCredits,
        loadLastPublicationsStatisticsAnalysis,
        getLastPublicationsStatisticsAnalysis,
        getUserSubscriptionRouteParams,
        managingUserMaxPublicationsByWeek
    };

};
